import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useSetting } from "../settings/SettingsContext";
import type { Driver, ListResponse, ServiceAlert, Stop, TripInstance, Vehicle } from "../api/types";
import { alertFix } from "../api/types";

interface AcknowledgedSet {
  [id: string]: number;
}

const ACK_KEY = "occ.panic.ack.v1";

function loadAck(): AcknowledgedSet {
  try {
    return JSON.parse(localStorage.getItem(ACK_KEY) ?? "{}");
  } catch {
    return {};
  }
}

function saveAck(ack: AcknowledgedSet) {
  localStorage.setItem(ACK_KEY, JSON.stringify(ack));
}

function unwrap<T>(value: ListResponse<T> | T[] | undefined | null): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray((value as ListResponse<T>).data)) return (value as ListResponse<T>).data;
  return [];
}

function pickLocation(a: ServiceAlert): { lat?: number; lon?: number } {
  // alertFix drops a 0,0 "fix" from a tablet with no GPS lock, so the queue
  // says "no location" rather than pointing at Null Island.
  const fix = alertFix(a.metadata);
  return { lat: fix?.lat, lon: fix?.lon };
}

function haversineMeters(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function nearestStopName(lat: number, lon: number, stops: Stop[]): string | null {
  let best: Stop | null = null;
  let bestDist = Infinity;
  for (const s of stops) {
    const d = haversineMeters({ lat, lon }, { lat: s.location.latitude, lon: s.location.longitude });
    if (d < bestDist) { bestDist = d; best = s; }
  }
  // Beyond ~800m the "nearest stop" isn't meaningfully "near" anymore.
  return best && bestDist <= 800 ? best.stop_name : null;
}

/** Multi-word station name -> initials ("Faiz Ahmed Faiz" -> "FAF"); short
 *  names (e.g. "N-5") pass through unchanged. Mirrors TripsPage's helper. */
function abbreviateStop(text: string): string {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return text.trim();
  return words.map((w) => w[0]).join("").toUpperCase();
}

function routeSpan(longName?: string): string | null {
  if (!longName) return null;
  const m = longName.match(/^(.*?)\s+to\s+(.*)$/i);
  if (m) return `${abbreviateStop(m[1])} → ${abbreviateStop(m[2])}`;
  return longName;
}

function fmtElapsed(startIso: string, nowMs: number): string {
  const startMs = new Date(startIso).getTime();
  if (Number.isNaN(startMs)) return "--:--";
  const totalSec = Math.max(0, Math.floor((nowMs - startMs) / 1000));
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function fmtClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

export default function PanicPage() {
  const { session } = useAuth();
  const refreshMs = useSetting<number>("occ.panic_refresh_ms", 5000);
  const [alerts, setAlerts] = useState<ServiceAlert[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [acked, setAcked] = useState<AcknowledgedSet>(loadAck);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  const [tripCache, setTripCache] = useState<Record<string, TripInstance>>({});
  const [now, setNow] = useState(() => Date.now());

  // Fetched once: the queue needs to name the bus/driver and locate the
  // nearest stop, and the alert only carries UUIDs + a raw lat/lon. A
  // failure here degrades to showing the UUID rather than taking the panic
  // queue down.
  useEffect(() => {
    let cancelled = false;
    api.listVehicles().then((v) => { if (!cancelled) setVehicles(unwrap(v)); }).catch(() => {});
    api.listDrivers().then((d) => { if (!cancelled) setDrivers(unwrap(d)); }).catch(() => {});
    api.listStops().then((s) => { if (!cancelled) setStops(unwrap(s)); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const data = await api.listAlerts({ type: "PANIC", limit: 100 });
        if (cancelled) return;
        setAlerts(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load");
      }
    }
    tick();
    const id = window.setInterval(tick, refreshMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [refreshMs]);

  const active = useMemo(() => alerts.filter((a) => a.is_active), [alerts]);
  const resolvedToday = useMemo(() => alerts.filter((a) => !a.is_active), [alerts]);

  const vehicleCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of vehicles) m.set(v.id, v.vehicle_id);
    return m;
  }, [vehicles]);

  const driverById = useMemo(() => {
    const m = new Map<string, Driver>();
    for (const d of drivers) m.set(d.id, d);
    return m;
  }, [drivers]);

  // Resolve the affected route for currently-active panics that carry a
  // trip_id — fetched on demand rather than for the whole history, since
  // there are only ever a handful of active alarms at once.
  useEffect(() => {
    const missingTripIds = Array.from(
      new Set(
        active
          .map((a) => a.metadata?.trip_id)
          .filter((id): id is string => !!id && !tripCache[id])
      )
    );
    if (missingTripIds.length === 0) return;
    let cancelled = false;
    Promise.all(missingTripIds.map((id) => api.getTripInstance(id).catch(() => null))).then((results) => {
      if (cancelled) return;
      setTripCache((prev) => {
        const next = { ...prev };
        missingTripIds.forEach((id, i) => {
          const t = results[i];
          if (t) next[id] = t;
        });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [active, tripCache]);

  function ack(id: string) {
    const next = { ...acked, [id]: Date.now() };
    setAcked(next);
    saveAck(next);
  }

  async function resolve(id: string) {
    const note = window.prompt("Resolution note (optional)", "");
    if (note === null) return; // cancelled
    try {
      const dispatcherName = session?.profile?.displayName || session?.employeeId || "Dispatcher";
      const text = note.trim() ? `${note.trim()} Resolved by ${dispatcherName}.` : `Resolved by ${dispatcherName}.`;
      await api.updateAlert(id, { description_text: text });
      await api.deactivateAlert(id);
      const data = await api.listAlerts({ type: "PANIC", limit: 100 });
      setAlerts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resolve");
    }
  }

  return (
    <div className="page panic-q-page">
      <div className="panic-q-header">
        <h1>Panic Queue</h1>
        <p className="secondary-text">Life-safety alarms from vehicles. Acknowledge, locate, resolve.</p>
      </div>
      {error && <div className="banner banner-error">{error}</div>}

      {active.length === 0 && resolvedToday.length === 0 && (
        <div className="card empty large">No panic alerts today.</div>
      )}

      {active.map((p) => {
        const { lat, lon } = pickLocation(p);
        const isAcked = !!acked[p.id];
        const vehicleId = p.metadata?.vehicle_id;
        const driverId = p.metadata?.driver_id;
        const busLabel = vehicleId ? vehicleCodeById.get(vehicleId) ?? vehicleId.slice(0, 8) + "…" : null;
        const driver = driverId ? driverById.get(driverId) : undefined;
        const driverName = driver ? `${driver.first_name} ${driver.last_name}`.trim() : null;
        const stationName = lat != null && lon != null ? nearestStopName(lat, lon, stops) : null;
        const trip = p.metadata?.trip_id ? tripCache[p.metadata.trip_id] : undefined;
        const span = routeSpan(trip?.route_long_name);
        const driverPhone = driver?.phone?.trim();

        const parts: string[] = [];
        parts.push(driverName ? `Driver ${driverName} triggered SOS` : "SOS triggered");
        if (stationName) parts.push(`near ${stationName}`);
        const line1 = parts.join(" ");
        const metaParts: string[] = [];
        if (span) metaParts.push(span);
        metaParts.push(`${fmtElapsed(p.created_at, now)} elapsed`);

        return (
          <div className="panic-q-banner" key={p.id}>
            <span className="panic-q-icon">!</span>
            <div className="panic-q-main">
              <div className="panic-q-title">PANIC ALARM{busLabel ? ` · ${busLabel}` : ""}</div>
              <div className="panic-q-sub">{line1} · {metaParts.join(" · ")}</div>
            </div>
            <div className="panic-q-timer mono">{fmtElapsed(p.created_at, now)}</div>
            <div className="panic-q-actions">
              {!isAcked ? (
                <button className="panic-q-btn panic-q-ack" type="button" onClick={() => ack(p.id)}>
                  Acknowledge
                </button>
              ) : (
                <button className="panic-q-btn panic-q-ack" type="button" onClick={() => resolve(p.id)}>
                  Resolve
                </button>
              )}
              {lat != null && lon != null ? (
                <a
                  className="panic-q-btn panic-q-locate"
                  target="_blank"
                  rel="noreferrer"
                  href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`}
                >
                  Locate
                </a>
              ) : (
                <span className="panic-q-btn panic-q-locate disabled" title="No location reported with this alarm">
                  Locate
                </span>
              )}
              {driverPhone ? (
                <a className="panic-q-btn panic-q-call" href={`tel:${driverPhone}`}>Call driver</a>
              ) : (
                <span className="panic-q-btn panic-q-call disabled" title="No phone number on file for this driver">
                  Call driver
                </span>
              )}
            </div>
          </div>
        );
      })}

      {resolvedToday.length > 0 && (
        <section className="card panic-q-resolved-card">
          <div className="panic-q-resolved-head">
            <h3><span className="panic-q-bar" />Earlier today</h3>
            <span className="panic-q-resolved-count">{resolvedToday.length} resolved</span>
          </div>
          <div className="panic-q-resolved-list">
            {resolvedToday.map((p) => {
              const vehicleId = p.metadata?.vehicle_id;
              const busLabel = vehicleId ? vehicleCodeById.get(vehicleId) ?? vehicleId.slice(0, 8) + "…" : null;
              return (
                <div className="panic-q-resolved-row" key={p.id}>
                  <span className="panic-q-resolved-pill">RESOLVED</span>
                  <div className="panic-q-resolved-main">
                    <div className="panic-q-resolved-title">Panic{busLabel ? ` · ${busLabel}` : ""}</div>
                    {p.description_text && (
                      <div className="panic-q-resolved-desc">{p.description_text}</div>
                    )}
                  </div>
                  <span className="panic-q-resolved-time">{fmtClock(p.updated_at || p.created_at)}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
